import React, { useState } from 'react';
import { useStyles } from '../../../../lib/styles';
import Typography from '../../../presentational/Typography';
import { months, constructNumberRange } from '../../../../../src/lib/dateUtilities';

import membershipSignupPanelStyles from './MembershipSignupPanel_styles';
import api from '../../../authenticate/Authenticate/api';
import Spacer from '../../Spacer';
import Button from '../../Button';
import Icon from '../../Icon';
import clsx from 'clsx';
import { isEmail } from '../../../../lib/validationUtils';
import { Pixel } from '../../Pixel';
import { MEMBERCENTRE_UPDATE_CLICK, raiseEvent } from '../../../../lib/gtm';
import { compose, withProps, withState } from 'recompose';
import { computeReferrer } from '../../../../lib/stringUtils';
import * as queryString from 'querystring';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import _ from 'lodash';
dayjs.extend(customParseFormat);

export interface MembershipSignupPanelProps {
  joinBackgroundImage: string;
  joinPersonImage: string;
  joinTitle: string;
  joinDescription: string;
  joinEmailTextboxPlaceholder: string;
  joinButtonText: string;
  joinConfirmationText: string;
  source: string | null;
  routingParams: {},
}

export const MembershipSignupPanel = (props: MembershipSignupPanelProps) => {
  const {
    joinPersonImage,
    joinTitle,
    joinDescription,
    joinEmailTextboxPlaceholder,
    joinButtonText,
    joinConfirmationText,
    source,
    routingParams,
  } = props;
  
  const styles = useStyles(membershipSignupPanelStyles(props));

  const [userEmail, setUserEmail] = useState('');
  const [userEmailIsValid, setUserEmailIsValid] = useState(true);
  const [firstName, setFirstName] = useState<any>('');
  const [lastName, setLastName] = useState<any>('');
  const [dobDay, setDOBDay] = useState<string>('14');
  const [dobMonth, setDOBMonth] = useState<string>('');
  const [dobYear, setDOBYear] = useState<string>('1966');
  
  const [isSaving, setIsSaving] = useState(false);

  const [submitSuccessful, setSubmitSuccessful] =
    useState<boolean | undefined>(undefined);
  const [submitMessage, setSubmitMessage] = useState('');

  const validate = () => {
    //  validate fields
    if (!userEmail || !isEmail(userEmail)) {
      setUserEmailIsValid(false);
      return { isValid: false, message: 'Please enter your email address.' };
    }

    if ((dobDay && dobMonth && dobYear)
      && !dayjs(`${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(2, '0')}`, 'YYYY-MM-DD', true).isValid()) {
      return { isValid: false, message: 'Oops. That DOB does not look right. Please try again.' };
    }

    return { isValid: true, message: '' };
  };

  const submitJoinNow = (e) => {
    e.preventDefault();

    const { isValid, message } = validate();
    if (!isValid) {
      setSubmitSuccessful(false);
      setSubmitMessage(message);
      return;
    };

    setIsSaving(true);
    setSubmitSuccessful(undefined);

    const subscriberDetails = {
      source,
    };

    if (firstName) { subscriberDetails['given_names'] = firstName; }
    if (lastName) { subscriberDetails['last_name'] = lastName; }
    if (dobDay && dobMonth && dobYear) {
      subscriberDetails['dob'] = `${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(2, '0')}`;
    };

    const dotDigitalDataFields: {[key: string]: string | null}[] = [
      {key: 'SOURCE', value: subscriberDetails.source},
    ];
    Object.keys(subscriberDetails).forEach((key) => {
      dotDigitalDataFields.push({
        key: key,
        value: subscriberDetails[key],
      });
    });

    return Promise.all(
      [
        api.collectSubscriber(
          userEmail, 
          subscriberDetails
        ), 
      ])
      .then(() => {
        setSubmitMessage(joinConfirmationText);
        setSubmitSuccessful(true);
        raiseEvent(MEMBERCENTRE_UPDATE_CLICK)
      })
      .catch((e) => {
        setSubmitMessage(e.message);
        setSubmitSuccessful(false);
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <>
      <div className={`signupWrap ${styles.container}`}>
        {submitSuccessful && (
          <Pixel firstname={firstName} lastname={lastName} email={userEmail} />
        )}

        <Spacer width={1160} space={['m', 'l']}>
          <Typography className={`signUpContainer ${styles.signUpContainer}`}>
            <div className={`signupPicture ${styles.signupPicture}`}>
              <img src={joinPersonImage} />
            </div>
            <div className={`signupText ${styles.signupText}`}>
              <form
                className={clsx(`needs-validation`)}
                onSubmit={submitJoinNow}
                id="memberSignupForm"
                noValidate
                autoComplete='off'
              >
                <div className={`membership-form-container ${styles.section}`}>
                  <Spacer width={1160} space={'m'}>
                    <Typography>
                      <h2>
                        <strong>{joinTitle}</strong>
                      </h2>
                      <p>{joinDescription}</p>
                      {!submitSuccessful && (
                        <>
                          <div className={`formGroupContainer ${styles.formGroupContainer}`}>
                            <div className={`formGroup ${styles.formGroup}`}>
                              <label
                                className="control-label"
                                htmlFor="profile-field-email"
                              >
                                Email <span style={{ color: 'red' }}>*</span>
                              </label>
                              <input
                                className={clsx(`form-control`, { 'is-invalid': !userEmailIsValid })}
                                id={`profile-field-email`}
                                type="text"
                                required
                                value={userEmail}
                                autoFocus
                                onChange={e => {
                                  const email = e.target.value;
                                  setUserEmail(email);
                                  setUserEmailIsValid(isEmail(email) && email != '');
                                }}
                              />
                            </div>
                            <div className={`formGroup ${styles.formGroup}`}>
                              <label
                                className="control-label"
                                htmlFor="profile-field-firstname"
                              >
                                First Name
                              </label>
                              <input
                                className={`form-control`}
                                id={`profile-field-firstname`}
                                type="text"
                                value={firstName}
                                onChange={e => setFirstName(e.target.value)}
                              />
                            </div>
                            <div className={`formGroup ${styles.formGroup}`}>
                              <label
                                className="control-label"
                                htmlFor="profile-field-lastname"
                              >
                                Last Name
                              </label>
                              <input
                                className={`form-control`}
                                id={`profile-field-lastname`}
                                type="text"
                                value={lastName}
                                onChange={e => setLastName(e.target.value)}
                              />
                            </div>
                            <div className={`formGroup ${styles.formGroup} ${styles.dobGroup}`}>
                              <label
                                className="control-label"
                                htmlFor="profile-field-dob"
                              >
                                Date of Birth{' '}
                              </label>
                              <Spacer
                                row
                                selfH={0}
                                width={`100%`}
                                className={`${styles.dobContainer}`}
                              >
                                <select
                                  className={`form-control pr-2`}
                                  value={dobDay}
                                  onChange={e => setDOBDay(e.target.value)}
                                >
                                  {Array.from({length: 31}, (_, i) => i + 1).map(key => (
                                    <option key={key} value={key}>
                                      {key}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  className={`form-control pr-2`}
                                  value={dobMonth}
                                  onChange={e => setDOBMonth(e.target.value)}
                                >
                                  <option value="">Month</option>
                                  {months.map((month, key) => (
                                    <option key={key} value={month.index}>
                                      {month.full}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  className={`form-control pr-2`}
                                  value={dobYear}
                                  onChange={e => setDOBYear(e.target.value)}
                                >
                                  {constructNumberRange(
                                    1930,
                                    parseInt(
                                      dayjs()
                                        .add(dayjs.duration({ years: -15 }))
                                        .format('YYYY')
                                    )
                                  )
                                    .reverse()
                                    .map((year, key) => (
                                      <option key={key} value={year}>
                                        {year}
                                      </option>
                                    ))}
                                </select>
                              </Spacer>
                            </div>
                          </div>
                          <div
                            className={
                              `membership-form-controls-container justify-content-start
                              ${styles.controlsContainer}`}
                          >
                            {isSaving && (
                              <Button disabled={true} onClick={() => {}}>
                                <Icon icon={'autorenew'} rotateIcon={true} className={'mr-2'} />
                                <span>Please wait...</span>
                              </Button>
                            )}
                            {!isSaving && (
                              <Button
                                type={'submit'}
                                onClick={() => {}}
                              >
                                <span>
                                  {joinButtonText}
                                </span>
                              </Button>
                            )}
                          </div>
                        </>
                      )}
                      </Typography>

                    {submitSuccessful === true && (
                      <div
                        className={`alert alert-success text-center ${styles.alert}`}
                        role="alert"
                      >
                        {submitMessage}
                        {_.has(routingParams, 'returnUrl') && (
                          <Button
                            className={`mx-auto mt-2`}
                            onClick={() => {
                              window.location.href = routingParams['returnUrl']?.toString() ?? '/';
                            }}
                          >
                            {routingParams['returnText']}
                          </Button>
                        )}
                      </div>
                    )}

                    {submitSuccessful === false && (
                      <div
                        className={`alert alert-danger text-center ${styles.alert}`}
                        role="alert"
                      >
                        {submitMessage}
                      </div>
                    )}
                  </Spacer>
                </div>
              </form>
            </div>
          </Typography>
        </Spacer>
      </div>
    </>
  );
};

export default compose(
  withState('source', 'setSource', 'website'),
  withProps(({ setSource }) => ({
    onMount: () => {
      setSource(computeReferrer());
    },
  })),
  withState('routingParams', 'setRoutingParams', () => queryString.parse(window.location.search.replace('?', ''))),
)(MembershipSignupPanel);